import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Gem, Package, Wallet } from 'lucide-react'
import { DotLottieReact } from '@lottiefiles/dotlottie-react'
import { motion } from 'motion/react'
import AnimatedNumber from '../components/AnimatedNumber'
import SkinCarousel from '../components/SkinCarousel'
import TasksList from '../components/TasksList'
import TermsAndInfo from '../components/TermsAndInfo'
import PushPrompt from '../components/PushPrompt'
import { OnboardingPlus } from '../components/OnboardingPlus'
import useAudioManager from '../hooks/useAudioManager'
import { HalloweenBanner } from '../features/event'
import { RushDropTeaser, RushDropWeeklyBanner } from '../features/rushdrop'
import { getAvailableWithdrawMethods } from '../features/withdraw'
import { TeamBanner } from '../features/team'
import useAppStore from '../store/useAppStore'
import { LOGO_SRC } from '../config/brand'
import GuestHome from './GuestHome'

// Поэтапное появление секций главной страницы
const SECTIONS_VARIANT = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } }
}

const SECTION_VARIANT = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } }
}

const MemberHome = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { balance, telegramUser, user } = useAppStore()
  const { playSound } = useAudioManager({
    pick: 'pick',
    select: 'select'
  })

  const hasPrivateCases = user?.has_private_cases
  const pendingPromo = window.sessionStorage.getItem('skinoz_pending_promo')

  // Баннер персонального кейса — переиспользуется на mobile и desktop
  const privateCaseBanner = (
    <button
      onClick={() => {
        playSound('select')
        navigate('/cases')
        // Сохраняем категорию для выбора после загрузки
        localStorage.setItem('selectedCaseCategory', 'private')
      }}
      className="flex w-full items-center gap-3 rounded-xl bg-gradient-to-r from-amber-500/20 via-orange-500/15 to-rose-500/20 border border-amber-500/30 p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.07)] transition-all active:scale-[0.98] hover:border-amber-500/50 lg:p-5"
    >
      <div className="shrink-0">
        <DotLottieReact
          src="/lottie/gift.lottie"
          loop
          autoplay
          className="h-12 w-12"
        />
      </div>
      <div className="text-left">
        <p className="font-bold text-amber-200">{t('cases.privateBanner')}</p>
        <p className="text-xs text-amber-200/70">{t('cases.privateBannerDesc')}</p>
      </div>
    </button>
  )

  const balanceCard = (telegramUser || user) && (
    <div
      className="gradient-border rounded-2xl p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_10px_15px_-3px_rgba(0,0,0,0.25),0_4px_6px_-4px_rgba(0,0,0,0.25)] backdrop-blur-md transition-all lg:p-5"
    >
      {/* Баланс — иконка + значение */}
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-accent-primary to-accent-secondary shadow-md shadow-accent-primary/20 lg:h-12 lg:w-12">
          <Gem size={20} className="text-dark-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs text-text-secondary lg:text-sm">{t('home.balance')}</p>
          <p className="bg-gradient-to-r from-accent-primary to-accent-secondary bg-clip-text text-3xl font-bold tabular-nums text-transparent lg:text-4xl">
            <AnimatedNumber value={balance} />
          </p>
        </div>
      </div>

      {/* Кнопки — строго равной ширины, без переноса */}
      <div className="mt-3 flex gap-2">
        <button
          onClick={() => {
            playSound('pick')
            navigate('/payment')
          }}
          className="flex-1 flex items-center justify-center gap-1.5 rounded-xl bg-gradient-to-r from-accent-primary to-accent-secondary bg-[length:200%_200%] animate-gradient-shift px-4 py-2.5 text-sm font-semibold text-dark-primary shadow-[inset_0_1px_0_rgba(255,255,255,0.25),0_4px_6px_-1px_rgba(0,255,127,0.2),0_2px_4px_-2px_rgba(0,255,127,0.2)] transition-opacity hover:opacity-90"
        >
          {/* <Plus size={16} /> */}
          <span>{t('home.topUp')}</span>
          {user?.best_promo?.percent > 0 && (
            <span className="font-bold">
              +{user.best_promo.percent}%
            </span>
          )}
        </button>
        {getAvailableWithdrawMethods(user).length > 0 && (
          <button
            onClick={() => {
              playSound('pick')
              navigate('/withdraw')
            }}
            className="flex-1 flex items-center justify-center gap-1.5 rounded-xl border border-white/15 bg-white/5 px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-white/10"
            title={t('home.withdraw', 'Вывести')}
          >
            <Wallet size={16} />
            <span>{t('home.withdraw', 'Вывести')}</span>
          </button>
        )}
      </div>
    </div>
  )

  return (
    <div className="text-white">
      {pendingPromo && (
        <button type="button" onClick={() => navigate(`/promo/${encodeURIComponent(pendingPromo)}`)} className="mx-auto block w-full max-w-xl rounded-xl border border-accent-primary/30 bg-surface p-4 text-accent-primary">
          {t('guest.pendingPromo')}
        </button>
      )}
      <SkinCarousel />

      <motion.div
        variants={SECTIONS_VARIANT}
        initial="hidden"
        animate="visible"
        className="space-y-6 px-6 py-6 lg:space-y-8 lg:px-10 lg:py-0 lg:pb-8 lg:px-0"
      >
        <motion.div variants={SECTION_VARIANT} className="pt-8 text-center lg:py-12 lg:hidden">
          <div className="relative mb-5 inline-block">
            <img
              src={LOGO_SRC}
              alt="SKINOZ"
              className="relative z-10 mx-auto w-full max-w-[280px] sm:max-w-[320px]"
            />
            <div className="pointer-events-none absolute left-1/2 top-full h-8 w-3/4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent-primary/30 blur-2xl" />
          </div>
          <p className="whitespace-pre-line bg-gradient-to-r from-white/90 to-accent-secondary bg-clip-text text-lg leading-relaxed text-transparent lg:mx-auto lg:max-w-3xl lg:text-xl">
            {/* {t('home.description')} */}
          </p>
        </motion.div>

        <motion.div variants={SECTION_VARIANT} className="lg:hidden">
          {balanceCard}
        </motion.div>

        {user?.onboarding_plus?.status === 'active' && (
          <motion.div variants={SECTION_VARIANT} className="empty:hidden">
            <OnboardingPlus />
          </motion.div>
        )}

        {/* Баннер эвента «Проклятие Skinoz» — появляется сам при наличии me.halloween_event (mobile) */}
        <motion.div variants={SECTION_VARIANT} className="lg:hidden">
          <HalloweenBanner />
        </motion.div>

        {/* Тизер Rush Drop — появляется сам при наличии эвента (mobile) */}
        <motion.div variants={SECTION_VARIANT} className="lg:hidden">
          <RushDropTeaser />
        </motion.div>

        {/* Баннер недельного розыгрыша — независим от эвента, появляется сам (mobile) */}
        <motion.div variants={SECTION_VARIANT} className="lg:hidden">
          <RushDropWeeklyBanner />
        </motion.div>

        {/* Баннер персонального кейса — только на mobile */}
        {hasPrivateCases && (
          <motion.div variants={SECTION_VARIANT} className="lg:hidden">
            {privateCaseBanner}
          </motion.div>
        )}

        {/* Вход в банды: своя банда или промо каталога (mobile) */}
        <motion.div variants={SECTION_VARIANT} className="lg:hidden">
          <TeamBanner />
        </motion.div>

        <motion.div variants={SECTION_VARIANT} className="space-y-6 lg:grid lg:grid-cols-12 lg:items-start lg:gap-6 lg:space-y-0">
          <div className="space-y-6 lg:col-span-8">
            <TasksList />
          </div>

          <div className="space-y-6 lg:col-span-4 lg:sticky lg:top-24">
            {/* Баннер эвента «Проклятие Skinoz» — desktop */}
            <div className="hidden lg:block">
              <HalloweenBanner />
            </div>

            {/* Тизер Rush Drop — desktop */}
            <div className="hidden lg:block">
              <RushDropTeaser />
            </div>

            {/* Баннер недельного розыгрыша — desktop */}
            <div className="hidden lg:block">
              <RushDropWeeklyBanner />
            </div>

            {/* Вход в банды — desktop */}
            <div className="hidden lg:block">
              <TeamBanner />
            </div>

            <div className="hidden pb-3 lg:pb-0">
              {/* Зелёная кнопка — всегда на mobile, на desktop только если нет персонального кейса */}
              <div className={hasPrivateCases ? 'lg:hidden' : ''}>
                <button
                  onClick={() => navigate('/cases')}
                  className="gradient-border-cta animate-gradient-shift flex w-full items-center justify-center gap-3 rounded-xl p-5 text-xl font-bold text-dark-primary shadow-[inset_0_1px_0_rgba(255,255,255,0.25),0_25px_50px_-12px_rgba(0,255,127,0.3)] transition-all active:scale-[0.98] hover:scale-[1.02] hover:opacity-90 lg:min-h-[124px] lg:flex-col lg:items-start"
                >
                  <Package size={28} />
                  <div className="text-left">
                    <div>{t('home.openCases')}</div>
                    <div className="text-sm font-normal opacity-90">{t('home.openCasesDescription')}</div>
                  </div>
                </button>
              </div>
              {/* Баннер персонального кейса — только на desktop */}
              {hasPrivateCases && (
                <div className="hidden lg:block">
                  {privateCaseBanner}
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>

      <TermsAndInfo />

      {/* Призыв включить push-уведомления (после онбординга, раз в 7 дней) */}
      <PushPrompt />
    </div>
  )
}

const Home = () => {
  const isGuest = useAppStore((state) => state.isGuest)
  return isGuest ? <GuestHome /> : <MemberHome />
}

export default Home
